/**
 * Boomfection Landing — Camera Module
 * Camera return button, tooltip events, field tooltip
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 *
 * SECURITY NOTE: This module uses only textContent for DOM updates — no innerHTML.
 * The btn.innerHTML assignment uses a static Unicode emoji literal, not user input.
 */

import { pillDescriptions } from './LandingGameEffects.js';
import { t } from '/js/i18n.js';

/** @private last hovered field ID for tooltip updates */
let lastHoveredFieldId = null;

/**
 * Returns the last hovered field ID (used by orchestrator for fieldUpdate checks)
 * @returns {number|null}
 */
export function getLastHoveredFieldId() {
    return lastHoveredFieldId;
}

/**
 * Creates the camera-return button and injects its styles.
 * The innerHTML assignment below is a static Unicode emoji literal, not user input.
 * @returns {HTMLElement}
 */
export function createCameraReturnBtn() {
    let btn = document.getElementById('camera-return-btn');
    if (btn) return btn;
    btn = document.createElement('button');
    btn.id = 'camera-return-btn';
    btn.className = 'camera-return-btn hidden';
    // Static emoji literal — no user input
    btn.textContent = '\u{1F3AF}';
    btn.title = t('tooltip.cameraReturn');
    document.body.appendChild(btn);

    const style = document.createElement('style');
    style.textContent = `
        .camera-return-btn {
            position: fixed;
            bottom: 80px;
            right: 20px;
            z-index: 1000;
            width: 48px;
            height: 48px;
            border-radius: 50%;
            border: 2px solid rgba(0, 255, 136, 0.6);
            background: rgba(20, 10, 40, 0.9);
            color: #fff;
            font-size: 22px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 0 15px rgba(0, 255, 136, 0.3);
            transition: opacity 0.3s, transform 0.3s;
            animation: cameraReturnPulse 2s ease-in-out infinite;
        }
        .camera-return-btn:hover {
            transform: scale(1.15);
            box-shadow: 0 0 25px rgba(0, 255, 136, 0.5);
        }
        .camera-return-btn.hidden {
            opacity: 0;
            pointer-events: none;
            transform: scale(0.8);
        }
        @keyframes cameraReturnPulse {
            0%, 100% { box-shadow: 0 0 15px rgba(0, 255, 136, 0.3); }
            50% { box-shadow: 0 0 25px rgba(0, 255, 136, 0.6); }
        }
    `;
    document.head.appendChild(style);
    return btn;
}

/**
 * Initializes the camera return button with click handler
 * @param {Object} client - MultiplayerClient instance
 */
export function initCameraReturnBtn(client) {
    const btn = createCameraReturnBtn();
    btn.addEventListener('click', () => {
        if (client.renderer?.restorePlayerCamera) {
            client.renderer.restorePlayerCamera(client.playerId);
        }
        hideCameraReturnBtn();
    });
}

/**
 * Shows the camera return button
 */
export function showCameraReturnBtn() {
    const btn = createCameraReturnBtn();
    btn.classList.remove('hidden');
}

/**
 * Hides the camera return button
 */
export function hideCameraReturnBtn() {
    const btn = document.getElementById('camera-return-btn');
    if (btn) btn.classList.add('hidden');
}

/**
 * Sets up renderer field-hover tooltip events
 * @param {Object} client - MultiplayerClient instance
 */
export function setupTooltipEvents(client) {
    if (!client.renderer) return;
    client.renderer.onFieldHover = (fieldId) => {
        lastHoveredFieldId = fieldId;
        updateFieldTooltip(fieldId, client);
    };
}

/**
 * Updates the field tooltip content
 * @param {number|null} fieldId
 * @param {Object} client - MultiplayerClient instance
 */
export function updateFieldTooltip(fieldId, client) {
    const fieldTooltip = document.getElementById('field-tooltip');
    if (!fieldTooltip) return;
    if (!client.game || fieldId === null) {
        fieldTooltip.classList.remove('visible');
        return;
    }
    const field = client.game.sphere.getField(fieldId);
    if (!field) {
        fieldTooltip.classList.remove('visible');
        return;
    }

    let ownerName = '-';
    if (field.ownerId) {
        const player = client.game.players.get(field.ownerId);
        ownerName = player ? player.name : field.ownerId;
    }

    document.getElementById('tooltip-viruses').textContent = field.value || 0;
    document.getElementById('tooltip-max').textContent = field.maxValue || '?';
    document.getElementById('tooltip-owner').textContent = ownerName;

    const pillRow = document.getElementById('tooltip-pill-row');
    const pillSpan = document.getElementById('tooltip-pill');
    if (field.pill) {
        pillRow.style.display = 'flex';
        const pillInfo = pillDescriptions[field.pill.type];
        pillSpan.textContent = pillInfo ? pillInfo.name : field.pill.type;
    } else {
        pillRow.style.display = 'none';
    }

    const previewRow = document.getElementById('tooltip-preview-row');
    const previewSpan = document.getElementById('tooltip-preview');
    if (field.ownerId && field.ownerId === client.playerId) {
        const wouldExplode = field.value + 1 > field.maxValue;
        previewRow.style.display = 'flex';
        if (wouldExplode) {
            previewSpan.textContent = t('tooltip.explosionPreview');
            previewSpan.className = 'tooltip-value explosion';
        } else {
            previewSpan.textContent = t('tooltip.virusPreview', { current: field.value + 1, max: field.maxValue });
            previewSpan.className = 'tooltip-value';
        }
    } else {
        previewRow.style.display = 'none';
    }

    fieldTooltip.classList.add('visible');
}
